/**
 * NotificationBell — header bell icon with unread count badge and a
 * dropdown of the last 15 notifications. Polls unread count every 60s.
 *
 * Renders "See all" link to /notifications for full history.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, Loader2, CheckCircle2 } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
} from "@/components/ui/dropdown-menu";
import { NOTIFICATION_KINDS } from "@/components/notifications/kinds";


export default function NotificationBell() {
  const nav = useNavigate();
  const [count, setCount] = useState(0);
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);

  const refreshCount = useCallback(async () => {
    try {
      const r = await api.get("/notifications/unread-count");
      setCount(r.data?.count || 0);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    refreshCount();
    const id = setInterval(refreshCount, 60000);
    return () => clearInterval(id);
  }, [refreshCount]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setRows(null);
      try {
        const r = await api.get("/notifications", { params: { limit: 15 } });
        if (!cancelled) setRows(r.data || []);
      } catch {
        if (!cancelled) setRows([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  async function markAllRead() {
    setMarkingAll(true);
    try {
      await api.post("/notifications/mark-all-read", {});
      setCount(0);
      setRows((rs) => (rs || []).map((r) => ({ ...r, read_at: r.read_at || new Date().toISOString() })));
    } catch { /* ignore */ }
    finally { setMarkingAll(false); }
  }

  async function openNotif(n) {
    if (!n.read_at) {
      try {
        await api.post(`/notifications/${n.id}/read`, {});
        setCount((c) => Math.max(0, c - 1));
      } catch { /* ignore */ }
    }
    setOpen(false);
    const link = NOTIFICATION_KINDS[n.kind]?.linkFor?.(n) || "/notifications";
    nav(link);
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="notification-bell"
          className="relative flex items-center justify-center h-8 w-8 rounded-md hover:bg-zinc-100 transition-colors"
          aria-label="Notifications"
        >
          <Bell className="h-4 w-4 text-zinc-700" strokeWidth={2} />
          {count > 0 && (
            <span
              className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-red-600 text-white text-2xs font-mono-num font-semibold grid place-items-center leading-none"
              data-testid="notification-bell-count"
            >
              {count > 99 ? "99+" : count}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 p-0">
        <div data-testid="notification-menu">
        <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-100">
          <div className="text-sm font-medium text-zinc-950">Notifications</div>
          {count > 0 && (
            <button
              onClick={markAllRead}
              disabled={markingAll}
              className="text-xs text-zinc-600 hover:text-zinc-900 underline inline-flex items-center gap-1"
              data-testid="notification-mark-all-read"
            >
              {markingAll ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
              Mark all read
            </button>
          )}
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {rows === null ? (
            <div className="px-4 py-6 text-xs text-zinc-500 flex items-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="px-4 py-8 text-center text-xs text-zinc-500">
              You&apos;re all caught up.
            </div>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {rows.map((n) => (
                <NotifRow key={n.id} notif={n} onClick={() => openNotif(n)} />
              ))}
            </ul>
          )}
        </div>
        <div className="border-t border-zinc-100 px-3 py-2 text-right">
          <Link
            to="/notifications"
            onClick={() => setOpen(false)}
            className="text-xs text-zinc-600 hover:text-zinc-900 underline"
            data-testid="notification-see-all"
          >
            See all notifications →
          </Link>
        </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}


function NotifRow({ notif, onClick }) {
  const meta = NOTIFICATION_KINDS[notif.kind] || NOTIFICATION_KINDS.default;
  const Icon = meta.icon;
  return (
    <li>
      <button
        onClick={onClick}
        className={`w-full text-left px-3 py-2 flex items-start gap-2 hover:bg-zinc-50 ${notif.read_at ? "opacity-60" : ""}`}
        data-testid={`notification-item-${notif.id}`}
      >
        <Icon className={`h-4 w-4 shrink-0 mt-0.5 ${meta.tone}`} strokeWidth={2} />
        <div className="min-w-0 flex-1">
          <div className="text-xs text-zinc-800 line-clamp-2">{notif.message}</div>
          <div className="text-2xs text-zinc-500 mt-0.5">
            {new Date(notif.created_at).toLocaleString()}
          </div>
        </div>
        {!notif.read_at && (
          <span className="h-2 w-2 rounded-full bg-blue-600 mt-1.5 shrink-0" />
        )}
      </button>
    </li>
  );
}
